import { effectiveTaxCode, taxCodeDefinition, type TaxCode } from '../taxCodes';
import type { Invoice } from '../types';
import type { SellerSettings } from './sellerSettings';
import { unitCodeOrDefault } from './units';

/**
 * XRechnung (EN 16931 / UBL Invoice 2.1) generator.
 *
 * German B2B e-invoicing: receiving structured invoices has been mandatory since 01.01.2025;
 * sending becomes mandatory 01.01.2027 for companies above 800.000 € prior-year turnover and
 * 01.01.2028 for everyone. This produces the outgoing side.
 *
 * The tax treatment is NOT re-derived here — it comes from the same tax table that drives the
 * DATEV booking (taxCodes.ts). One source, so the XML and the Buchungsstapel cannot disagree
 * about what was taxed how.
 *
 * GEPRUEFT (04.09.2026) gegen den offiziellen KoSIT-Pruefer 1.6.3 mit der Konfiguration
 * XRechnung 3.0.2 (2026-08-31): sechs Beispielbelege -- Dienstleistung, Maschinenverkauf mit
 * Rabatt, gemischte Steuersaetze, Miete mit Leistungszeitraum, Gutschrift und oeffentlicher
 * Auftraggeber -- werden alle angenommen ("Validation successful").
 *
 * Der Lauf foerderte VIER Fehler zutage, die ohne ihn niemand gesehen haette; sie stehen als
 * Kommentar an der jeweiligen Stelle und als Test in xrechnung.test.ts. Ablauf zum Wiederholen:
 * docs/howto/e-rechnung.md. Nach JEDER Aenderung an diesem Erzeuger erneut laufen lassen --
 * eine XRechnung, die der Pruefer abweist, gilt beim Empfaenger als nicht gestellte Rechnung.
 */
export interface EInvoiceBuyer {
  readonly name: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly countryCode: string;
  /** BT-48, required for reverse charge and intra-community supply. */
  readonly vatId: string;
  readonly email: string;
  /** BT-10 Buyer reference — mandatory in XRechnung (Leitweg-ID for B2G, order reference for B2B). */
  readonly buyerReference: string;
}

/** Invoice type codes (BT-3, UNTDID 1001). */
export const invoiceTypeCodes = { invoice: '380', creditNote: '381' } as const;

/**
 * Spezifikationskennung (BT-24) der XRechnung 3.0.
 *
 * BEFUND (09/2026, gefunden mit dem KoSIT-Prüfer): Hier stand
 * `…#urn:xoev-de:kosit:standard:xrechnung_3.0` -- die Form VOR Fassung 3.0. Der offizielle Prüfer
 * wies daraufhin jeden Beleg mit „Es wird empfohlen das Dokument zurückzuweisen. Da kein
 * Pruefszenario gegriffen hat" ab: Die Kennung wählt das Prüfszenario aus; passt sie nicht, wird
 * gar nicht erst inhaltlich geprüft, sondern rundheraus abgelehnt. Jede bis dahin von FORQ
 * erzeugte XRechnung wäre beim Empfänger durchgefallen.
 *
 * Der Wert steht wörtlich so in der `scenarios.xml` der KoSIT-Prüfkonfiguration.
 */
export const XRECHNUNG_CUSTOMIZATION_ID =
  'urn:cen.eu:en16931:2017#compliant#urn:xeinkauf.de:kosit:xrechnung_3.0';

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function amount(value: number): string {
  return value.toFixed(2);
}

function quantity(value: number): string {
  return value.toFixed(3);
}

interface TaxSubtotal {
  readonly taxCode: TaxCode;
  readonly taxableAmount: number;
  readonly taxAmount: number;
}

/** VAT breakdown (BG-23) — one entry per tax category and rate, as EN 16931 requires. */
export function taxSubtotals(invoice: Invoice): readonly TaxSubtotal[] | null {
  const buckets = new Map<TaxCode, { taxableAmount: number; taxAmount: number }>();
  for (const line of invoice.lines) {
    const taxCode = effectiveTaxCode(line);
    if (!taxCode) return null;
    const bucket = buckets.get(taxCode) ?? { taxableAmount: 0, taxAmount: 0 };
    bucket.taxableAmount += line.netAmount;
    bucket.taxAmount += line.netAmount * (taxCodeDefinition(taxCode).rate / 100);
    buckets.set(taxCode, bucket);
  }
  return [...buckets.entries()].map(([taxCode, sums]) => ({
    taxCode,
    taxableAmount: Math.round(sums.taxableAmount * 100) / 100,
    taxAmount: Math.round(sums.taxAmount * 100) / 100,
  }));
}

/**
 * Elektronische Adresse (BT-34 Verkäufer / BT-49 Käufer) -- in der XRechnung PFLICHT.
 *
 * BEFUND (09/2026, gefunden mit dem KoSIT-Prüfer): Sie fehlte auf beiden Seiten. Der Prüfer
 * beanstandete jeden Beleg mit PEPPOL-EN16931-R020 („Seller electronic address MUST be provided")
 * und R010 für den Käufer -- Schema und die EN-16931-Regeln waren dagegen von Anfang an sauber.
 *
 * `schemeID="EM"` ist die E-Mail-Adresse aus der Codeliste EAS. Sie ist der Weg, über den der
 * Empfänger die Rechnung tatsächlich bekommt, und für einen Mittelständler ohne Peppol-Anschluss
 * die einzige, die er ehrlich angeben kann.
 *
 * ACHTUNG Reihenfolge: `cbc:EndpointID` ist in UBL das ERSTE Kind von `cac:Party` -- vor der
 * Anschrift. An anderer Stelle scheitert schon die Schemaprüfung.
 */
function endpointXml(email: string): string {
  const wert = email.trim();
  if (!wert) return '';
  return `      <cbc:EndpointID schemeID="EM">${escapeXml(wert)}</cbc:EndpointID>\n`;
}

function partyXml(tag: string, party: {
  name: string; street: string; postalCode: string; city: string; countryCode: string; vatId: string;
}, extra: string, endpointEmail: string): string {
  return `  <cac:${tag}>
    <cac:Party>
${endpointXml(endpointEmail)}      <cac:PostalAddress>
        <cbc:StreetName>${escapeXml(party.street)}</cbc:StreetName>
        <cbc:CityName>${escapeXml(party.city)}</cbc:CityName>
        <cbc:PostalZone>${escapeXml(party.postalCode)}</cbc:PostalZone>
        <cac:Country>
          <cbc:IdentificationCode>${escapeXml(party.countryCode)}</cbc:IdentificationCode>
        </cac:Country>
      </cac:PostalAddress>
${party.vatId ? `      <cac:PartyTaxScheme>
        <cbc:CompanyID>${escapeXml(party.vatId)}</cbc:CompanyID>
        <cac:TaxScheme><cbc:ID>VAT</cbc:ID></cac:TaxScheme>
      </cac:PartyTaxScheme>
` : ''}      <cac:PartyLegalEntity>
        <cbc:RegistrationName>${escapeXml(party.name)}</cbc:RegistrationName>
      </cac:PartyLegalEntity>
${extra}    </cac:Party>
  </cac:${tag}>`;
}

/**
 * Leistungszeitraum (BG-14) -- nur wenn die Leistung wirklich über einen Zeitraum ging, sonst gilt
 * das einzelne Leistungsdatum (BT-72, siehe deliveryXml).
 *
 * ACHTUNG Reihenfolge: UBL schreibt die Reihenfolge der Kindelemente fest vor. `cac:InvoicePeriod`
 * gehört NACH `cbc:BuyerReference` und VOR `cac:AccountingSupplierParty` -- an anderer Stelle
 * scheitert schon die Schemaprüfung, lange bevor eine Geschäftsregel greift.
 */
function invoicePeriodXml(invoice: Invoice): string {
  const from = invoice.serviceDate;
  const to = invoice.serviceEndDate;
  if (!from || !to || to === from) return '';
  return `  <cac:InvoicePeriod>
    <cbc:StartDate>${from}</cbc:StartDate>
    <cbc:EndDate>${to}</cbc:EndDate>
  </cac:InvoicePeriod>
`;
}

/**
 * Leistungsdatum (BT-72) als tatsächliches Lieferdatum.
 *
 * Pflichtangabe nach § 14 Abs. 4 Nr. 6 UStG -- bis 09/2026 trug FORQ es weder auf dem Papier noch
 * in der XML. Bewusst BT-72 und NICHT BT-7 (`cbc:TaxPointDate`): die XRechnung-Spezifikation der
 * KoSIT untersagt BT-7 ausdrücklich. Geht die Leistung über einen Zeitraum, trägt ihn zusätzlich
 * BG-14; das Enddatum bleibt dann auch hier das maßgebliche Lieferdatum.
 *
 * ACHTUNG Reihenfolge: `cac:Delivery` gehört NACH `cac:AccountingCustomerParty` und VOR
 * `cac:PaymentMeans`.
 */
function deliveryXml(invoice: Invoice): string {
  const delivered = invoice.serviceEndDate || invoice.serviceDate;
  if (!delivered) return '';
  return `  <cac:Delivery>
    <cbc:ActualDeliveryDate>${delivered}</cbc:ActualDeliveryDate>
  </cac:Delivery>
`;
}

/**
 * Zeilenrabatt als Abschlag (BG-27) -- BT-138 Prozentsatz, BT-136 Betrag, BT-137 Grundlage.
 *
 * WARUM als eigener Abschlag und nicht über einen gesenkten Einzelpreis: EN 16931 verlangt, dass
 * der Zeilennettobetrag (BT-131) aus Menge mal Einzelpreis MINUS der Zeilenabschläge hervorgeht.
 * Seit die Rechnung einen Zeilenrabatt trägt (09/2026) wäre die alte XML rechnerisch falsch
 * gewesen: sie führte den Listenpreis und daneben den bereits rabattierten Nettobetrag, ohne den
 * Abschlag zu nennen -- die Prüfung hätte das als Rechenfehler beanstandet.
 *
 * Der Betrag wird als Differenz gebildet (Grundlage minus Nettobetrag), nicht neu aus dem Prozent-
 * satz gerechnet. Nur so stimmt die Zeile auf den Cent mit dem gedruckten Beleg überein.
 */
function lineAllowanceXml(line: Invoice['lines'][number]): string {
  const discountPercent = line.discountPercent ?? 0;
  if (discountPercent <= 0) return '';
  const baseAmount = Math.round(line.quantity * line.unitPrice * 100) / 100;
  const allowance = Math.round((baseAmount - line.netAmount) * 100) / 100;
  if (allowance <= 0) return '';
  return `    <cac:AllowanceCharge>
      <cbc:ChargeIndicator>false</cbc:ChargeIndicator>
      <cbc:AllowanceChargeReasonCode>95</cbc:AllowanceChargeReasonCode>
      <cbc:AllowanceChargeReason>Rabatt</cbc:AllowanceChargeReason>
      <cbc:MultiplierFactorNumeric>${discountPercent.toFixed(2)}</cbc:MultiplierFactorNumeric>
      <cbc:Amount currencyID="EUR">${amount(allowance)}</cbc:Amount>
      <cbc:BaseAmount currencyID="EUR">${amount(baseAmount)}</cbc:BaseAmount>
    </cac:AllowanceCharge>
`;
}

/**
 * Builds the XRechnung XML. Returns null when the invoice carries a rate that has no tax code —
 * refusing beats emitting an invoice whose tax treatment was invented.
 */
export function buildXRechnung(invoice: Invoice, seller: SellerSettings, buyer: EInvoiceBuyer): string | null {
  const subtotals = taxSubtotals(invoice);
  if (!subtotals) return null;
  if (!invoice.invoiceNumber || !invoice.issueDate) return null;

  const isCredit = invoice.kind === 'credit_note';
  const typeCode = isCredit ? invoiceTypeCodes.creditNote : invoiceTypeCodes.invoice;
  const netTotal = Math.round(invoice.netTotal * 100) / 100;
  const vatTotal = Math.round(subtotals.reduce((sum, entry) => sum + entry.taxAmount, 0) * 100) / 100;
  const grossTotal = Math.round((netTotal + vatTotal) * 100) / 100;

  const sellerExtra = `      <cac:Contact>
        <cbc:Name>${escapeXml(seller.contactName)}</cbc:Name>
        <cbc:Telephone>${escapeXml(seller.contactPhone)}</cbc:Telephone>
        <cbc:ElectronicMail>${escapeXml(seller.contactEmail)}</cbc:ElectronicMail>
      </cac:Contact>
`;
  const buyerExtra = buyer.email
    ? `      <cac:Contact>
        <cbc:ElectronicMail>${escapeXml(buyer.email)}</cbc:ElectronicMail>
      </cac:Contact>
`
    : '';

  const taxSubtotalXml = subtotals.map((entry) => {
    const definition = taxCodeDefinition(entry.taxCode);
    return `    <cac:TaxSubtotal>
      <cbc:TaxableAmount currencyID="EUR">${amount(entry.taxableAmount)}</cbc:TaxableAmount>
      <cbc:TaxAmount currencyID="EUR">${amount(entry.taxAmount)}</cbc:TaxAmount>
      <cac:TaxCategory>
        <cbc:ID>${definition.xrechnungCategory}</cbc:ID>
        <cbc:Percent>${definition.rate.toFixed(2)}</cbc:Percent>
${definition.xrechnungExemptionReason ? `        <cbc:TaxExemptionReason>${escapeXml(definition.xrechnungExemptionReason)}</cbc:TaxExemptionReason>\n` : ''}        <cac:TaxScheme><cbc:ID>VAT</cbc:ID></cac:TaxScheme>
      </cac:TaxCategory>
    </cac:TaxSubtotal>`;
  }).join('\n');

  const lineTag = isCredit ? 'CreditNoteLine' : 'InvoiceLine';
  const quantityTag = isCredit ? 'CreditedQuantity' : 'InvoicedQuantity';
  const lineXml = invoice.lines.map((line, index) => {
    const taxCode = effectiveTaxCode(line)!;
    const definition = taxCodeDefinition(taxCode);
    return `  <cac:${lineTag}>
    <cbc:ID>${index + 1}</cbc:ID>
    <cbc:${quantityTag} unitCode="${unitCodeOrDefault(line.unit)}">${quantity(line.quantity)}</cbc:${quantityTag}>
    <cbc:LineExtensionAmount currencyID="EUR">${amount(line.netAmount)}</cbc:LineExtensionAmount>
${lineAllowanceXml(line)}    <cac:Item>
      <cbc:Name>${escapeXml(line.description)}</cbc:Name>
      <cac:ClassifiedTaxCategory>
        <cbc:ID>${definition.xrechnungCategory}</cbc:ID>
        <cbc:Percent>${definition.rate.toFixed(2)}</cbc:Percent>
        <cac:TaxScheme><cbc:ID>VAT</cbc:ID></cac:TaxScheme>
      </cac:ClassifiedTaxCategory>
    </cac:Item>
    <cac:Price>
      <cbc:PriceAmount currencyID="EUR">${amount(line.unitPrice)}</cbc:PriceAmount>
    </cac:Price>
  </cac:${lineTag}>`;
  }).join('\n');

  // Eine Gutschrift ist in UBL ein EIGENES Dokument (CreditNote), keine Rechnung mit Typcode 381.
  // BEFUND (09/2026, KoSIT-Pruefer): BR-CL-01 -- der Typcode 381 ist in einem Invoice-Dokument
  // nicht zugelassen. Wurzelelement, Namensraum, Typcode-, Zeilen- und Mengenelement heissen
  // deshalb je nach Belegart anders; alles andere ist Wort fuer Wort dasselbe.
  const wurzel = isCredit ? 'CreditNote' : 'Invoice';
  const typeCodeTag = isCredit ? 'CreditNoteTypeCode' : 'InvoiceTypeCode';
  return `<?xml version="1.0" encoding="UTF-8"?>
<ubl:${wurzel} xmlns:ubl="urn:oasis:names:specification:ubl:schema:xsd:${wurzel}-2"
             xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2"
             xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2">
  <cbc:CustomizationID>${XRECHNUNG_CUSTOMIZATION_ID}</cbc:CustomizationID>
  <cbc:ProfileID>urn:fdc:peppol.eu:2017:poacc:billing:01:1.0</cbc:ProfileID>
  <cbc:ID>${escapeXml(invoice.invoiceNumber)}</cbc:ID>
  <cbc:IssueDate>${invoice.issueDate}</cbc:IssueDate>
${!isCredit && invoice.dueDate ? `  <cbc:DueDate>${invoice.dueDate}</cbc:DueDate>\n` : ''}  <cbc:${typeCodeTag}>${typeCode}</cbc:${typeCodeTag}>
  <cbc:DocumentCurrencyCode>EUR</cbc:DocumentCurrencyCode>
  <cbc:BuyerReference>${escapeXml(buyer.buyerReference)}</cbc:BuyerReference>
${invoicePeriodXml(invoice)}${partyXml('AccountingSupplierParty', seller, sellerExtra, seller.contactEmail)}
${partyXml('AccountingCustomerParty', buyer, buyerExtra, buyer.email)}
${deliveryXml(invoice)}  <cac:PaymentMeans>
    <cbc:PaymentMeansCode>58</cbc:PaymentMeansCode>
    <cac:PayeeFinancialAccount>
      <cbc:ID>${escapeXml(seller.iban.replace(/\s/g, ''))}</cbc:ID>
${seller.bankName ? `      <cbc:Name>${escapeXml(seller.bankName)}</cbc:Name>\n` : ''}    </cac:PayeeFinancialAccount>
  </cac:PaymentMeans>
  <cac:PaymentTerms>
    <cbc:Note>Zahlbar innerhalb von ${invoice.paymentTermDays} Tagen ohne Abzug.</cbc:Note>
  </cac:PaymentTerms>
  <cac:TaxTotal>
    <cbc:TaxAmount currencyID="EUR">${amount(vatTotal)}</cbc:TaxAmount>
${taxSubtotalXml}
  </cac:TaxTotal>
  <cac:LegalMonetaryTotal>
    <cbc:LineExtensionAmount currencyID="EUR">${amount(netTotal)}</cbc:LineExtensionAmount>
    <cbc:TaxExclusiveAmount currencyID="EUR">${amount(netTotal)}</cbc:TaxExclusiveAmount>
    <cbc:TaxInclusiveAmount currencyID="EUR">${amount(grossTotal)}</cbc:TaxInclusiveAmount>
    <cbc:PayableAmount currencyID="EUR">${amount(grossTotal)}</cbc:PayableAmount>
  </cac:LegalMonetaryTotal>
${lineXml}
</ubl:${wurzel}>`;
}

export function xRechnungFileName(invoice: Invoice): string {
  return `XRechnung_${invoice.invoiceNumber ?? invoice.proposalNumber}.xml`;
}

export function downloadXRechnung(xml: string, fileName: string): void {
  const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
